import React, { CSSProperties, useCallback, useMemo, useRef } from "react";
import styled, { DefaultTheme, useTheme } from "styled-components";
import { LanguageNameSpaces } from "@shared/types";
import { hexToRGB } from "@shared/utils/misc";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import useMeasure from "utils/hooks/useMeasure";
import {
  PRICE_LADDER_FOCUS_ID,
  PRICE_LADDER_ID,
  PRICE_LADDER_LABEL_ID,
  PRICE_LADDER_MARKER_ID,
  PRICE_LADDER_PRICE_ID,
  PRICE_LADDER_TICK_ID,
  PRICE_LADDER_ZONE_ID,
} from "testIds";
import { NEW_LOOK_FIGURE_WEIGHT } from "common/NewLook/tokens.ts";
import { LadderKind, LevelsLadderModel } from "common/NewLook/priceLadder.ts";
import { textWidth } from "common/NewLook/measureText.ts";
import { shortZeros } from "common/NewLook/shortZeros.ts";
import {
  getTradeBarLayout,
  getTradeBarPrice,
  TradeBarBand,
  TradeBarLabelLine,
  TradeBarLabelPart,
  TradeBarLane,
  TradeBarLayout,
  TradeBarTick,
} from "common/NewLook/tradeBarLayout.ts";
import {
  getTradeBarTargetX,
  TradeBarTarget,
} from "common/NewLook/tradeBarHover.ts";
import useTradeBarInteraction from "common/NewLook/useTradeBarInteraction.ts";
import TradeBarDetails from "common/NewLook/TradeBarDetails.tsx";
import TradeBarHoverCard from "common/NewLook/TradeBarHoverCard.tsx";
import TradeBarSheet from "common/NewLook/TradeBarSheet.tsx";
import useLadderPriceChange from "common/NewLook/useLadderPriceChange.tsx";

interface TradeBarProps {
  model: LevelsLadderModel;
  formatPrice: (price: number) => string;
  background: string;
  title: string;
  showLabels: boolean;
  showPrice: boolean;
  priceChange?: number;
  toneColor: (theme: DefaultTheme, tone: LadderKind) => string;
  className?: string;
  style?: CSSProperties;
  selection?: TradeBarTarget;
  onSelect?: (target: TradeBarTarget) => void;
}

interface TradeBarFocus {
  x: number;
  color: string;
  isPrice: boolean;
}

const TradeBar = (props: TradeBarProps) => {
  const {
    model,
    formatPrice: formatFullPrice,
    background,
    title,
    showLabels,
    showPrice,
    priceChange,
    toneColor,
    className,
    style,
    selection,
    onSelect,
  } = props;
  const theme = useTheme();
  const [measureRef, measured] = useMeasure<HTMLDivElement | null>();
  const width = measured?.width || 0;
  const texts = useTradeBarTexts();
  const formatPrice = useCallback(
    (price: number) => shortZeros(formatFullPrice(price)),
    [formatFullPrice]
  );
  const measure = usePartMeasure();
  const layout = useMemo(
    () =>
      width
        ? getTradeBarLayout(model, {
            width,
            formatPrice,
            measure: measure.part,
            texts,
            showLabels,
          })
        : undefined,
    [model, width, formatPrice, measure, texts, showLabels]
  );
  const barHeight = layout?.hasLanes ? LANES_HEIGHT : TRACK_HEIGHT;
  const interaction = useTradeBarInteraction(model, layout, onSelect);
  const activeTarget = onSelect ? selection : interaction.hover;
  const tone = (kind: LadderKind) => toneColor(theme, kind);
  const focus =
    layout && activeTarget && getFocus(layout, activeTarget, tone, theme);
  return (
    <>
      <Wrapper
        ref={measureRef}
        className={className}
        style={style}
        data-testid={PRICE_LADDER_ID}
        data-direction={model.isShort ? "short" : "long"}
        $isScrub={!!onSelect}
        {...(onSelect
          ? {}
          : {
              role: "button",
              tabIndex: 0,
              "aria-label": title,
              "aria-haspopup": "dialog" as const,
            })}
        {...interaction.handlers}
      >
        <MeasureLine ref={measure.ref} aria-hidden={true}>
          <Part $muted={false} $keyword={false} />
          <Part $muted={false} $keyword={true} />
        </MeasureLine>
        {showPrice && layout && layout.markerX !== undefined && (
          <CurrentPrice
            layout={layout}
            text={formatPrice(getTradeBarPrice(model) as number)}
            width={width}
            priceChange={priceChange}
          />
        )}
        <Bar style={{ height: barHeight }}>
          {layout && (
            <BarContent
              layout={layout}
              background={background}
              toneColor={tone}
              focus={focus}
            />
          )}
        </Bar>
        {layout && layout.rows > 0 && (
          <Labels style={{ height: layout.rows * ROW_HEIGHT }}>
            {layout.lines.map((line) => (
              <LabelLine
                key={line.key}
                line={line}
                toneColor={(tone) => toneColor(theme, tone)}
              />
            ))}
          </Labels>
        )}
      </Wrapper>
      {layout && (
        <TradeBarOverlays
          props={props}
          layout={layout}
          interaction={interaction}
          focus={focus}
          formatPrice={formatPrice}
          tone={tone}
        />
      )}
    </>
  );
};

export default React.memo(TradeBar);

const TradeBarOverlays = ({
  props,
  layout,
  interaction,
  focus,
  formatPrice,
  tone,
}: {
  props: TradeBarProps;
  layout: TradeBarLayout;
  interaction: ReturnType<typeof useTradeBarInteraction>;
  focus?: TradeBarFocus;
  formatPrice: (price: number) => string;
  tone: (kind: LadderKind) => string;
}) => (
  <EventBoundary {...STOP_PROPAGATION}>
    {interaction.hover && focus && (
      <TradeBarHoverCard anchor={interaction.anchor} x={focus.x}>
        <TradeBarDetails
          model={props.model}
          points={layout.points}
          target={interaction.hover}
          formatPrice={formatPrice}
          toneColor={tone}
          size="card"
        />
      </TradeBarHoverCard>
    )}
    {interaction.sheet && (
      <TradeBarSheet
        open={interaction.sheet.open}
        onClose={interaction.closeSheet}
        model={props.model}
        points={layout.points}
        target={interaction.sheet.target}
        onTargetChange={interaction.setSheetTarget}
        formatPrice={formatPrice}
        toneColor={tone}
        bar={
          <TradeBar
            {...props}
            showLabels={true}
            showPrice={true}
            className={undefined}
            style={undefined}
            selection={interaction.sheet.target}
            onSelect={interaction.setSheetTarget}
          />
        }
      />
    )}
  </EventBoundary>
);

const getFocus = (
  layout: TradeBarLayout,
  target: TradeBarTarget,
  tone: (kind: LadderKind) => string,
  theme: DefaultTheme
): TradeBarFocus | undefined => {
  const x = getTradeBarTargetX(layout.points, target);
  if (x === undefined) return;
  const isPrice = target.kind === "price";
  return {
    x,
    color: isPrice ? theme.colors.titleColor : tone(target.kind as LadderKind),
    isPrice,
  };
};

const useTradeBarTexts = () => {
  const { t } = useTypedTranslation(LanguageNameSpaces.TRADES);
  return useMemo(
    () => ({
      entry: t("phone.card_entry"),
      takeProfit: t("phone.card_take_profit"),
      stop: t("phone.card_stop"),
      noStop: t("phone.card_no_stop"),
      noTakeProfit: t("phone.card_no_take_profit"),
    }),
    [t]
  );
};

const usePartMeasure = () => {
  const ref = useRef<HTMLSpanElement | null>(null);
  const part = useCallback((labelPart: TradeBarLabelPart) => {
    const element = ref.current?.children[labelPart.tone ? 1 : 0];
    if (!element) return textWidth(labelPart.text);
    element.textContent = labelPart.text;
    return Math.ceil(element.getBoundingClientRect().width) + 1;
  }, []);
  return useMemo(() => ({ ref, part }), [part]);
};

const CurrentPrice = ({
  layout,
  text,
  width,
  priceChange,
}: {
  layout: TradeBarLayout;
  text: string;
  width: number;
  priceChange?: number;
}) => {
  const change = useLadderPriceChange(priceChange);
  const size = textWidth(text);
  const markerX = layout.markerX as number;
  const left = Math.max(
    0,
    Math.min(width - size - change.width, markerX - size / 2)
  );
  return (
    <PriceRow>
      <PriceText style={{ left }}>
        <span data-testid={PRICE_LADDER_PRICE_ID}>{text}</span>
        {change.element}
      </PriceText>
    </PriceRow>
  );
};

const BarContent = ({
  layout,
  background,
  toneColor,
  focus,
}: {
  layout: TradeBarLayout;
  background: string;
  toneColor: (tone: LadderKind) => string;
  focus?: TradeBarFocus;
}) => {
  const theme = useTheme();
  const lanes: TradeBarLane[] = layout.hasLanes
    ? ["top", "bottom"]
    : ["single"];
  const trackStyle = {
    left: layout.isStopOffScale ? ARROW_WIDTH : 0,
    right: layout.isTakeProfitOffScale ? ARROW_WIDTH : 0,
    background: hexToRGB(theme.colors.secondaryGrey, 0.18),
  };
  return (
    <>
      {lanes.map((lane) => (
        <Track key={lane} style={{ ...trackStyle, ...LANE_BOXES[lane] }} />
      ))}
      {layout.bands.map((band) => (
        <Band key={band.key} band={band} color={toneColor(band.tone)} />
      ))}
      {layout.isStopOffScale && (
        <Arrow $side="start" style={{ borderRightColor: toneColor("stop") }} />
      )}
      {layout.isTakeProfitOffScale && (
        <Arrow
          $side="end"
          style={{ borderLeftColor: toneColor("take_profit") }}
        />
      )}
      {layout.ticks.map((tick) => (
        <Tick key={tick.key} tick={tick} toneColor={toneColor} />
      ))}
      {focus && (
        <FocusHalo
          $isPrice={focus.isPrice}
          data-testid={PRICE_LADDER_FOCUS_ID}
          style={{
            left: focus.x,
            top: (layout.hasLanes ? LANES_HEIGHT : TRACK_HEIGHT) / 2,
            background: hexToRGB(focus.color, focus.isPrice ? 0.14 : 0.2),
            color: focus.color,
            ["--focus-ring" as string]: background,
          }}
        />
      )}
      {layout.markerX !== undefined && (
        <Marker
          data-testid={PRICE_LADDER_MARKER_ID}
          $isClosed={layout.isClosed}
          $isFocused={!!focus?.isPrice}
          style={{
            left: layout.markerX,
            top: (layout.hasLanes ? LANES_HEIGHT : TRACK_HEIGHT) / 2,
            boxShadow: markerShadow(
              background,
              layout.markerRing && toneColor(layout.markerRing)
            ),
          }}
        />
      )}
    </>
  );
};

const Band = ({ band, color }: { band: TradeBarBand; color: string }) => (
  <BandFill
    data-testid={PRICE_LADDER_ZONE_ID}
    data-tone={band.tone}
    style={{
      ...LANE_BOXES[band.lane],
      left: Math.min(band.from, band.to),
      width: Math.max(Math.abs(band.to - band.from), MIN_BAND_WIDTH),
      background: hexToRGB(color, band.isSolid ? 0.6 : 0.28),
    }}
  />
);

const Tick = ({
  tick,
  toneColor,
}: {
  tick: TradeBarTick;
  toneColor: (tone: LadderKind) => string;
}) => {
  const box = TICK_BOXES[tick.lane];
  const background = tick.isSplit
    ? `linear-gradient(${toneColor("stop")} 50%, ${toneColor("entry")} 50%)`
    : toneColor(tick.tone);
  return (
    <TickMark
      data-testid={PRICE_LADDER_TICK_ID}
      data-tone={tick.tone}
      style={{ left: tick.x, ...box, background }}
    />
  );
};

const LabelLine = ({
  line,
  toneColor,
}: {
  line: TradeBarLabelLine;
  toneColor: (tone: LadderKind) => string;
}) => (
  <Line
    style={{ left: line.left, top: line.row * ROW_HEIGHT }}
    data-testid={PRICE_LADDER_LABEL_ID}
    data-key={line.key}
  >
    {line.parts.map((part, index) => (
      <React.Fragment key={index}>
        {index > 0 && " "}
        <Part
          $muted={!!part.isMuted}
          $keyword={!!part.tone}
          style={part.tone ? { color: toneColor(part.tone) } : undefined}
        >
          {part.text}
        </Part>
      </React.Fragment>
    ))}
  </Line>
);

const markerShadow = (background: string, ring?: string) =>
  ring
    ? `0 0 0 2px ${background}, 0 0 0 4px ${ring}`
    : `0 0 0 2px ${background}`;

const stopPropagation = (event: React.SyntheticEvent) =>
  event.stopPropagation();
const STOP_PROPAGATION = {
  onClick: stopPropagation,
  onPointerDown: stopPropagation,
  onPointerMove: stopPropagation,
  onPointerUp: stopPropagation,
  onTouchStart: stopPropagation,
  onTouchMove: stopPropagation,
  onTouchEnd: stopPropagation,
  onKeyDown: stopPropagation,
};

const TRACK_HEIGHT = 6;
const LANES_HEIGHT = 10;
const ARROW_WIDTH = 9;
const ROW_HEIGHT = 17;
const MIN_BAND_WIDTH = 3;
const LANE_BOXES: Record<TradeBarLane, CSSProperties> = {
  single: { top: 0, height: 6 },
  top: { top: 0, height: 4 },
  bottom: { top: 6, height: 4 },
};
const TICK_BOXES: Record<TradeBarTick["lane"], CSSProperties> = {
  single: { top: -4, height: 14 },
  full: { top: -4, height: 18 },
  top: { top: -3, height: 9 },
  bottom: { top: 4, height: 9 },
};

const Wrapper = styled.div<{ $isScrub: boolean }>`
  position: relative;
  width: 100%;
  min-width: 0;
  outline-offset: 4px;
  border-radius: 4px;
  ${(p) => p.$isScrub && "touch-action: none; cursor: pointer;"}
`;
const EventBoundary = styled.div`
  display: contents;
`;
const PriceRow = styled.div`
  position: relative;
  height: 18px;
  margin-bottom: 6px;
`;
const PriceText = styled.span`
  position: absolute;
  top: 0;
  color: ${(p) => p.theme.colors.titleColor};
  font-size: 11.5px;
  line-height: 18px;
  font-weight: ${NEW_LOOK_FIGURE_WEIGHT};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`;
const Bar = styled.div`
  position: relative;
  margin: 4px 0;
`;
const Track = styled.span`
  position: absolute;
  border-radius: ${(p) => p.theme.radii.pill}px;
`;
const BandFill = styled.span`
  position: absolute;
`;
const Arrow = styled.span<{ $side: "start" | "end" }>`
  position: absolute;
  top: 50%;
  ${(p) => (p.$side === "start" ? "left: 0;" : "right: 0;")}
  width: 0;
  height: 0;
  transform: translateY(-50%);
  border-top: 6px solid transparent;
  border-bottom: 6px solid transparent;
  ${(p) =>
    p.$side === "start"
      ? "border-right: 9px solid;"
      : "border-left: 9px solid;"}
`;
const TickMark = styled.span`
  position: absolute;
  width: 3px;
  margin-left: -1.5px;
  border-radius: 1px;
  z-index: 1;
`;
const FocusHalo = styled.span<{ $isPrice: boolean }>`
  position: absolute;
  width: ${(p) => (p.$isPrice ? 26 : 22)}px;
  height: ${(p) => (p.$isPrice ? 26 : 22)}px;
  margin: ${(p) => (p.$isPrice ? "-13px 0 0 -13px" : "-11px 0 0 -11px")};
  border-radius: 50%;
  z-index: ${(p) => (p.$isPrice ? 1 : 3)};
  pointer-events: none;
  transition: left 120ms ease-out;
  ${(p) =>
    !p.$isPrice &&
    `
    &::after {
      content: "";
      position: absolute;
      inset: 6px;
      border-radius: 50%;
      background: currentColor;
      box-shadow: 0 0 0 2px var(--focus-ring);
    }
  `}
`;
const Marker = styled.span<{ $isClosed: boolean; $isFocused: boolean }>`
  position: absolute;
  width: ${(p) => (p.$isClosed ? 10 : 12)}px;
  height: ${(p) => (p.$isClosed ? 10 : 12)}px;
  margin: ${(p) => (p.$isClosed ? "-5px 0 0 -5px" : "-6px 0 0 -6px")};
  border-radius: ${(p) => (p.$isClosed ? "2px" : "50%")};
  background: ${(p) => p.theme.colors.titleColor};
  z-index: 2;
  pointer-events: none;
  transform: ${(p) => (p.$isFocused ? "scale(1.2)" : "none")};
  transition: transform 120ms ease-out;
`;
const MeasureLine = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  visibility: hidden;
  pointer-events: none;
  font-size: 11.5px;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
`;
const Labels = styled.div`
  position: relative;
  margin-top: 6px;
`;
const Line = styled.span`
  position: absolute;
  display: inline-flex;
  gap: 3px;
  white-space: nowrap;
  font-size: 11.5px;
  line-height: 17px;
  font-variant-numeric: tabular-nums;
`;
const Part = styled.span<{ $muted: boolean; $keyword: boolean }>`
  color: ${(p) =>
    p.$muted ? p.theme.colors.secondaryText : p.theme.colors.titleColor};
  font-weight: ${(p) =>
    p.$keyword ? p.theme.typography.fontWeights.MEDIUM : "normal"};
`;
